import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Eye, 
  EyeOff,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { leavesApi, Holiday, HolidayRequest } from '../../services/api/leaves';
import toast from 'react-hot-toast';

interface HolidayManagementProps {
  className?: string;
}

interface HolidayFormData {
  name: string;
  description: string;
  date: string;
  type: 'fixed' | 'floating' | 'optional';
  isRecurring: boolean;
  color: string;
}

const defaultFormData: HolidayFormData = {
  name: '',
  description: '',
  date: '',
  type: 'fixed',
  isRecurring: true,
  color: '#EF4444'
};

const holidayColors = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#EAB308', // Yellow
  '#22C55E', // Green
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6B7280', // Gray
];

const HolidayManagement: React.FC<HolidayManagementProps> = ({ className = '' }) => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [formData, setFormData] = useState<HolidayFormData>(defaultFormData);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const response = await leavesApi.getHolidays({
        year: selectedYear,
        limit: 100,
        sortBy: 'date',
        sortOrder: 'asc'
      });
      setHolidays(response.holidays);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const holidayData: HolidayRequest = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        date: new Date(formData.date).toISOString(),
        type: formData.type as any,
        isRecurring: formData.isRecurring,
        color: formData.color
      };

      if (editingHoliday) {
        await leavesApi.updateHoliday(editingHoliday._id, holidayData);
        toast.success('Holiday updated successfully');
      } else {
        await leavesApi.createHoliday(holidayData);
        toast.success('Holiday created successfully');
      }

      setShowForm(false);
      setEditingHoliday(null);
      setFormData(defaultFormData);
      fetchHolidays();
    } catch (error: any) {
      console.error('Error saving holiday:', error);
      toast.error(error.response?.data?.message || 'Failed to save holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      description: holiday.description || '',
      date: new Date(holiday.date).toISOString().split('T')[0],
      type: holiday.type,
      isRecurring: holiday.isRecurring,
      color: holiday.color
    });
    setShowForm(true);
  };

  const handleDelete = async (holiday: Holiday) => {
    if (!confirm(`Are you sure you want to delete "${holiday.name}"?`)) {
      return;
    }

    try {
      await leavesApi.deleteHoliday(holiday._id);
      toast.success('Holiday deleted successfully');
      fetchHolidays();
    } catch (error: any) {
      console.error('Error deleting holiday:', error);
      toast.error(error.response?.data?.message || 'Failed to delete holiday');
    }
  };

  const handleToggleStatus = async (holiday: Holiday) => {
    try {
      await leavesApi.toggleHolidayStatus(holiday._id);
      toast.success(`Holiday ${holiday.isActive ? 'deactivated' : 'activated'} successfully`);
      fetchHolidays();
    } catch (error: any) {
      console.error('Error toggling holiday status:', error);
      toast.error(error.response?.data?.message || 'Failed to update holiday status');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingHoliday(null);
    setFormData(defaultFormData);
  };

  const createDefaultHolidays = async () => {
    try {
      setSaving(true);
      const holidays = await leavesApi.createDefaultHolidays(selectedYear);

      // Show appropriate message based on response
      if (holidays && holidays.length > 0) {
        toast.success(`${holidays.length} default holidays created successfully`);
      } else {
        toast.info(`Default holidays for ${selectedYear} already exist`);
      }

      fetchHolidays();
    } catch (error: any) {
      console.error('Error creating default holidays:', error);

      // Handle specific error cases
      if (error.response?.status === 409) {
        toast.error(`Default holidays for ${selectedYear} already exist`);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create default holidays');
      }
    } finally {
      setSaving(false);
    }
  };

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i - 2);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Holiday Management</span>
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Manage company holidays that will be displayed in attendance calendars
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button
            onClick={createDefaultHolidays}
            disabled={saving}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Add Default Holidays
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Holiday</span>
          </button>
        </div>
      </div>

      {/* Holiday Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">
              {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
            </h4>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Holiday Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Christmas Day"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                placeholder="Optional description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="fixed">Fixed (Same date every year)</option>
                  <option value="floating">Floating (Different date each year)</option>
                  <option value="optional">Optional</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex space-x-2">
                  {holidayColors.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                      className={`w-8 h-8 rounded-full border-2 ${
                        formData.color === color ? 'border-gray-800' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isRecurring}
                    onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Recurring yearly</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center space-x-2"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                <Save className="h-4 w-4" />
                <span>{editingHoliday ? 'Update' : 'Create'} Holiday</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Holidays List */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h4 className="text-lg font-medium text-gray-900">
            Holidays for {selectedYear}
          </h4>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
            <p className="text-gray-600 mt-2">Loading holidays...</p>
          </div>
        ) : holidays.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No holidays found for {selectedYear}</p>
            <p className="text-sm text-gray-500 mt-1">
              Click "Add Holiday" to create your first holiday
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {holidays.map((holiday) => (
              <div key={holiday._id} className="p-6 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: holiday.color }}
                  />
                  <div>
                    <h5 className="font-medium text-gray-900">{holiday.name}</h5>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                      <span>{new Date(holiday.date).toLocaleDateString()}</span>
                      <span className="capitalize">{holiday.type}</span>
                      {holiday.isRecurring && (
                        <span className="text-blue-600">Recurring</span>
                      )}
                    </div>
                    {holiday.description && (
                      <p className="text-sm text-gray-600 mt-1">{holiday.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleToggleStatus(holiday)}
                    className={`p-2 rounded-md ${
                      holiday.isActive
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={holiday.isActive ? 'Active' : 'Inactive'}
                  >
                    {holiday.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button
                    onClick={() => handleEdit(holiday)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                    title="Edit holiday"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(holiday)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                    title="Delete holiday"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HolidayManagement;
